// Enrolment hub — the one place an owner grabs everything needed to get the
// loyalty card onto a customer's phone: the self-enrolment link (/q/join), a
// branded scannable QR, a live preview of the real page + the wallet card,
// one-tap sharing, and printable counter placards.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Copy, Download, ExternalLink, Loader2, MessageCircle, Printer, RotateCcw, Save, Share2 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { buildBrandedQr, QR_LOGO_SCALE_DEFAULT } from '@/lib/brandedQr'
import { useBrandQrLogo } from '@/hooks/useBrandQrLogo'
import { LoyaltyScreenPreview } from './LoyaltyScreenPreview'

function normHex(s: string): string {
  const m = (s || '').trim().replace(/^#/, '')
  if (m.length === 3) return '#' + m.split('').map((c) => c + c).join('')
  if (/^[0-9a-fA-F]{6}$/.test(m)) return '#' + m.toLowerCase()
  return ''
}

// DEFAULT_PAGE_BG mirrors defaultClaimBackground in the backend's
// marketing_loyalty_theme.go — the warm cream a tenant that has configured
// nothing still gets. Kept in sync by eye; the backend is the authority, so
// a drift here only mis-labels the swatch, it can't change the live page.
const DEFAULT_PAGE_BG = '#faf7f2'

// A short palette of backgrounds that read well behind the enrol form —
// warm neutrals, cool neutrals, and two dark options (the backend inverts
// the whole neutral ramp when the chosen background is dark).
const BG_PRESETS: Array<{ hex: string; label: string }> = [
  { hex: DEFAULT_PAGE_BG, label: 'Warm cream (default)' },
  { hex: '#ffffff', label: 'White' },
  { hex: '#f5f5f4', label: 'Stone' },
  { hex: '#f0f4f8', label: 'Cool grey' },
  { hex: '#f0fdf4', label: 'Mint' },
  { hex: '#fdf2f8', label: 'Blush' },
  { hex: '#1c1917', label: 'Espresso' },
  { hex: '#0b1220', label: 'Midnight' },
]

export function AdminEnrolmentSettings() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const qrRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)
  const [posterPending, setPosterPending] = useState<string | null>(null)
  const { logoDataUrl, brandedQrEnabled } = useBrandQrLogo()

  const linksQuery = useQuery({
    queryKey: ['public-links'],
    queryFn: () => apiClient.getPublicLinks(),
    staleTime: 60_000,
  })
  const settingsQuery = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 60_000,
  })

  // The enrol page's own colours live in the loyalty config (they also drive
  // /q/loyalty and the wallet pass), so this panel reads and writes that blob
  // rather than the receipt-branding settings.
  const loyaltyQuery = useQuery({
    queryKey: ['loyalty-config'],
    queryFn: () => apiClient.getLoyaltyConfig(),
    staleTime: 60_000,
  })

  const baseUrl = linksQuery.data?.data?.resolved?.url ?? ''
  const joinUrl = baseUrl ? `${baseUrl.replace(/\/+$/, '')}/q/join` : ''

  const branding = useMemo(() => {
    const d = settingsQuery.data?.data as Record<string, unknown> | undefined
    return {
      businessName: typeof d?.receipt_business_name === 'string' ? d.receipt_business_name : '',
      logoUrl: typeof d?.receipt_logo_url === 'string' ? d.receipt_logo_url : '',
      accentColor: typeof d?.receipt_accent_color === 'string' ? d.receipt_accent_color : '',
    }
  }, [settingsQuery.data])

  const cfg = loyaltyQuery.data?.data

  // Saved values, with the same precedence the backend applies:
  // loyalty override → receipt branding → built-in default.
  const savedAccent = normHex(cfg?.screen_accent_color || '') || normHex(branding.accentColor) || '#d97706'
  const savedBg = normHex(cfg?.screen_background_color || '') || DEFAULT_PAGE_BG

  // Draft colours. null = "not touched since the last load/save", so a
  // config arriving late (or a save landing) doesn't clobber a live edit.
  const [draftAccent, setDraftAccent] = useState<string | null>(null)
  const [draftBg, setDraftBg] = useState<string | null>(null)
  // Bumped after a save to force the live-preview iframe to re-fetch the
  // server-rendered page — it has no client-side state to invalidate.
  const [previewNonce, setPreviewNonce] = useState(0)

  const pageAccent = draftAccent ?? savedAccent
  const pageBg = draftBg ?? savedBg
  const coloursDirty = pageAccent !== savedAccent || pageBg !== savedBg

  const saveColours = useMutation({
    mutationFn: async () => {
      if (!cfg) throw new Error('Loyalty settings are still loading')
      // Store "" — the inherit sentinel — when the chosen colour is already
      // what the fallback would produce. That keeps a later change to the
      // receipt accent flowing through to these pages instead of being
      // frozen the first time anyone touches this panel.
      const receiptAccent = normHex(branding.accentColor) || '#d97706'
      return apiClient.updateLoyaltyConfig({
        ...cfg,
        screen_accent_color: pageAccent === receiptAccent ? '' : pageAccent,
        screen_background_color: pageBg === DEFAULT_PAGE_BG ? '' : pageBg,
      })
    },
    onSuccess: () => {
      setDraftAccent(null)
      setDraftBg(null)
      setPreviewNonce((n) => n + 1)
      queryClient.invalidateQueries({ queryKey: ['loyalty-config'] })
      toast({ title: 'Page colours saved', description: 'The live enrol page below now uses them.' })
    },
    onError: (err) =>
      toast({
        title: 'Could not save colours',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      }),
  })

  const accent = pageAccent
  const name = branding.businessName || 'Your cafe'

  // One QR definition for both the on-screen preview and the download. These
  // used to be two separate object literals, which meant the printed PNG could
  // silently drift from what the owner approved on screen; size and quiet-zone
  // are the only things allowed to differ.
  function enrolmentQr(size: number, margin: number) {
    return buildBrandedQr({
      data: joinUrl,
      size,
      style: {
        dark_color: '#1a1410',
        light_color: '#ffffff',
        corner_dot_color: accent,
        margin_modules: margin,
        dots_type: 'rounded',
        corner_square_type: 'extra-rounded',
        corner_dot_type: 'dot',
        error_correction: 'M',
      },
      logoDataUrl,
      logoEnabled: brandedQrEnabled,
      logoScale: QR_LOGO_SCALE_DEFAULT,
    })
  }

  // Render the branded QR into the page.
  useEffect(() => {
    const el = qrRef.current
    if (!el) return
    el.innerHTML = ''
    if (!joinUrl) return
    enrolmentQr(224, 8).append(el)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinUrl, accent, logoDataUrl, brandedQrEnabled])

  function copyLink() {
    if (!joinUrl) {
      toast({ title: 'Set your public URL first', variant: 'destructive' })
      return
    }
    navigator.clipboard
      .writeText(joinUrl)
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
        toast({ title: 'Link copied' })
      })
      .catch(() => toast({ title: 'Clipboard denied', variant: 'destructive' }))
  }

  function openLive() {
    if (joinUrl) window.open(joinUrl, '_blank', 'noopener')
  }

  function shareWhatsApp() {
    if (!joinUrl) {
      toast({ title: 'Set your public URL first', variant: 'destructive' })
      return
    }
    const text = `Join our loyalty card — collect stamps and earn rewards. Add it to your phone: ${joinUrl}`
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener')
  }

  function copyInstagram() {
    if (!joinUrl) {
      toast({ title: 'Set your public URL first', variant: 'destructive' })
      return
    }
    const caption = `📲 Add our loyalty card to your phone — collect stamps, earn rewards. Tap the link: ${joinUrl}`
    navigator.clipboard
      .writeText(caption)
      .then(() =>
        toast({ title: 'Caption + link copied', description: 'Paste it into your Instagram bio or story.' }),
      )
      .catch(() => toast({ title: 'Clipboard denied', variant: 'destructive' }))
  }

  async function downloadQr() {
    if (!joinUrl) {
      toast({ title: 'Set your public URL first', variant: 'destructive' })
      return
    }
    try {
      const blob = await enrolmentQr(1024, 24).getRawData('png')
      if (!(blob instanceof Blob)) throw new Error('no blob')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'loyalty-enrolment-qr.png'
      a.click()
      URL.revokeObjectURL(url)
      toast({ title: 'QR downloaded', description: 'Test it on a couple of phones before bulk printing.' })
    } catch {
      toast({ title: 'Export failed', description: 'Could not generate the PNG.', variant: 'destructive' })
    }
  }

  async function openPoster(layout: 'a4' | 'tent' | 'card') {
    setPosterPending(layout)
    try {
      const html = await apiClient.fetchLoyaltyPosterHTML(layout)
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const w = window.open(url, '_blank', 'noopener')
      if (!w) {
        toast({
          title: 'Pop-up blocked',
          description: 'Allow pop-ups for this site and try again.',
          variant: 'destructive',
        })
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      toast({
        title: 'Could not open placard',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    } finally {
      setPosterPending(null)
    }
  }

  return (
    <div className="max-w-5xl space-y-6">
      {/* Link + share */}
      <Card>
        <CardHeader>
          <CardTitle>Your enrolment link</CardTitle>
          <CardDescription>
            Share this so customers add the loyalty card to their phone. It opens a quick name + phone
            form, then their card with the <span className="font-medium">Add to Apple / Google Wallet</span>{' '}
            buttons.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!baseUrl && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Set your public URL in <span className="font-medium">Settings → Public links</span> first — the
              link and QR below need it.
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              readOnly
              value={joinUrl || '—'}
              onFocus={(e) => e.currentTarget.select()}
              className="h-9 flex-1 font-mono text-sm"
            />
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={copyLink} disabled={!joinUrl}>
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={openLive} disabled={!joinUrl}>
                <ExternalLink className="h-3.5 w-3.5" />
                Open
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={shareWhatsApp} disabled={!joinUrl}>
              <MessageCircle className="h-3.5 w-3.5" />
              Share on WhatsApp
            </Button>
            <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={copyInstagram} disabled={!joinUrl}>
              <Share2 className="h-3.5 w-3.5" />
              Copy for Instagram
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* QR */}
        <Card>
          <CardHeader>
            <CardTitle>Scannable QR</CardTitle>
            <CardDescription>Stick it on tables and the counter — scanning opens the enrolment page.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-3">
            <div ref={qrRef} className="rounded-lg border bg-white p-2" />
            <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={downloadQr} disabled={!joinUrl}>
              <Download className="h-3.5 w-3.5" />
              Download PNG
            </Button>
          </CardContent>
        </Card>

        {/* Pass mockup */}
        <Card>
          <CardHeader>
            <CardTitle>What customers get</CardTitle>
            <CardDescription>The enrol screen and the wallet card, themed to your brand (sample stamps).</CardDescription>
          </CardHeader>
          <CardContent>
            <LoyaltyScreenPreview
              accent={accent}
              logoUrl={cfg?.screen_logo_url || branding.logoUrl || undefined}
              pageLogoUrl={
                cfg?.screen_page_logo_url || cfg?.screen_logo_url || branding.logoUrl || undefined
              }
              headline={name}
              businessName={name}
              balance={3}
              threshold={7}
              rewardLabel="Free dessert"
            />
          </CardContent>
        </Card>
      </div>

      {/* Page colours — the enrol page is server-rendered, so these two
          values are the whole knob: the backend derives ink, card surface,
          borders, field fills and the reward pill from them (and inverts the
          neutrals when the background is dark). */}
      <Card>
        <CardHeader>
          <CardTitle>Page colours</CardTitle>
          <CardDescription>
            Brand the enrol page. Everything else — text, card, borders, the reward pill — is worked out
            from these two so the page stays readable, including on a dark background. These colours also
            apply to the scan screen and the wallet pass.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="enrol-page-bg" className="text-sm">Background</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={pageBg}
                  onChange={(e) => setDraftBg(normHex(e.target.value) || DEFAULT_PAGE_BG)}
                  className="h-9 w-12 shrink-0 cursor-pointer rounded border border-input bg-background p-1"
                  aria-label="Page background colour"
                />
                <Input
                  id="enrol-page-bg"
                  value={pageBg}
                  onChange={(e) => {
                    const v = normHex(e.target.value)
                    if (v) setDraftBg(v)
                  }}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {BG_PRESETS.map((p) => (
                  <button
                    key={p.hex}
                    type="button"
                    title={p.label}
                    aria-label={p.label}
                    onClick={() => setDraftBg(p.hex)}
                    className={`h-6 w-6 rounded-full border transition ${
                      pageBg === p.hex ? 'ring-2 ring-primary ring-offset-1' : 'border-border/70'
                    }`}
                    style={{ background: p.hex }}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="enrol-page-accent" className="text-sm">Accent</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={pageAccent}
                  onChange={(e) => setDraftAccent(normHex(e.target.value) || savedAccent)}
                  className="h-9 w-12 shrink-0 cursor-pointer rounded border border-input bg-background p-1"
                  aria-label="Accent colour"
                />
                <Input
                  id="enrol-page-accent"
                  value={pageAccent}
                  onChange={(e) => {
                    const v = normHex(e.target.value)
                    if (v) setDraftAccent(v)
                  }}
                  className="font-mono text-sm"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Buttons, links, the reward pill and the stamp dots.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              onClick={() => saveColours.mutate()}
              disabled={!coloursDirty || saveColours.isPending || !cfg}
            >
              {saveColours.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {coloursDirty ? 'Save colours' : 'Saved'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => {
                setDraftBg(DEFAULT_PAGE_BG)
                setDraftAccent(normHex(branding.accentColor) || '#d97706')
              }}
              disabled={saveColours.isPending}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to default
            </Button>
            {coloursDirty && (
              <span className="text-sm text-muted-foreground">
                Unsaved — the preview below updates once you save.
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Live page */}
      <Card>
        <CardHeader>
          <CardTitle>Live enrol page</CardTitle>
          <CardDescription>
            Exactly what opens when a customer scans the QR.{' '}
            {joinUrl && (
              <button type="button" className="font-medium underline underline-offset-2" onClick={openLive}>
                Open full screen ↗
              </button>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {joinUrl ? (
            <iframe
              // key forces a remount after a colour save — the page is
              // server-rendered, so a re-fetch is the only way to refresh it.
              key={previewNonce}
              title="Live enrolment page"
              src={joinUrl}
              className="mx-auto h-[560px] w-full max-w-[360px] rounded-2xl border bg-white shadow-sm"
            />
          ) : (
            <p className="text-sm text-muted-foreground">Set your public URL to preview the live page.</p>
          )}
        </CardContent>
      </Card>

      {/* Placard */}
      <Card>
        <CardHeader>
          <CardTitle>Printable counter placard</CardTitle>
          <CardDescription>A ready-to-print poster carrying the loyalty QR — pick a layout to open and print.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {(['a4', 'tent', 'card'] as const).map((layout) => (
            <Button
              key={layout}
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => openPoster(layout)}
              disabled={posterPending !== null}
            >
              <Printer className="h-3.5 w-3.5" />
              {layout === 'a4' ? 'A4 poster' : layout === 'tent' ? 'Table tent' : 'Card'}
            </Button>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
